import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { Alert, Button, Card, Flex, Heading, Text, TextField, View } from '@aws-amplify/ui-react';
import { FiCopy } from 'react-icons/fi';

interface AuthErrorProps {
    error?: any;
    auth: typeof Auth;
}

const AuthError: React.FC<AuthErrorProps> = ({ error, auth }) => {
    const [userSub, setUserSub] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        // Try to get user info even if authentication failed
        auth.currentUserInfo()
            .then(info => {
                if (info?.attributes?.sub) {
                    setUserSub(info.attributes.sub);
                }
            })
            .catch(() => {
                // If that fails, try to get from partial session
                auth.currentSession()
                    .then(session => {
                        const idToken = session.getIdToken();
                        const payload = idToken.decodePayload();
                        if (payload?.sub) {
                            setUserSub(payload.sub);
                        }
                    })
                    .catch(() => {
                        console.log('Unable to retrieve user sub');
                    });
            });
    }, [auth]);

    const handleCopy = () => {
        if (userSub) {
            navigator.clipboard.writeText(userSub);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleSignIn = () => {
        window.location.href = '/signIn';
    };

    return (
        <View className="App">
            <View className="App-header"></View>
            <Flex
                direction="column"
                justifyContent="center"
                alignItems="center"
                className="App-intro"
                padding="2rem"
            >
                <Card variation="elevated" width="100%" maxWidth="600px">
                    <Flex direction="column" gap="1rem" padding="2rem">
                        <Heading level={2} color="red">
                            Authentication Error
                        </Heading>

                        <Alert
                            variation="error"
                            isDismissible={false}
                            hasIcon={true}
                            heading="Unable to complete sign in"
                        >
                            This usually means your account needs to be activated by an administrator.
                        </Alert>

                        {userSub && (
                            <Flex direction="column" gap="0.5rem">
                                <Text fontWeight="bold">Your User ID (sub):</Text>
                                <Flex gap="0.5rem" alignItems="center">
                                    <TextField
                                        label=""
                                        labelHidden={true}
                                        value={userSub}
                                        isReadOnly={true}
                                        width="100%"
                                    />
                                    <Button
                                        onClick={handleCopy}
                                        size="small"
                                        variation="primary"
                                    >
                                        <FiCopy /> {copied ? "Copied!" : "Copy"}
                                    </Button>
                                </Flex>
                                <Text fontSize="small" color="neutral.80">
                                    Please send this ID to your administrator to request access.
                                </Text>
                            </Flex>
                        )}

                        {error && (
                            <View>
                                <Text fontSize="small" color="neutral.80">
                                    Error details: {error.message || JSON.stringify(error)}
                                </Text>
                            </View>
                        )}

                        <Flex direction="column" gap="0.5rem" marginTop="1rem">
                            <Button
                                variation="primary"
                                onClick={handleSignIn}
                                width="100%"
                            >
                                Try Again
                            </Button>
                        </Flex>

                        <Text fontSize="small" color="neutral.80" textAlign="center">
                            If you continue to experience issues, please contact support.
                        </Text>
                    </Flex>
                </Card>
            </Flex>
        </View>
    );
};

export default AuthError;
